import React, { FC, useCallback, useContext, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { RolesFetcher } from '../../fetchers/RolesFetcher';
import { IServerSidePaginatedFetcherParams, useAutoFetch } from '../../contexts/Fetcher';
import { AutocompleteServersideController } from '../InputControllers/AutocompleteController';
import { useForm } from 'react-hook-form';
import { apiOrganization } from '../../resources/api/apiOrganization';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { AxiosRequestConfig } from 'axios';
import { SelectController } from '../InputControllers/SelectController';
import Button from '@mui/material/Button';
import { CloseDialogFunc } from '../../hooks/useCloseDialog';
import { apiUser } from '../../resources/api/apiUser';
import { UserFullInfoFetcher } from '../../fetchers/UserFullInfoFetcher';
import { useSearchableDropdown } from '../../hooks/useSearchableDropdown';

interface IProps {
  isOpen: boolean;
  closeModal: CloseDialogFunc;
}

interface IFormData {
  organization: any;
  role_id: string;
}

const { useGetOrganizationsSilent } = apiOrganization;
const { useAssignRole } = apiUser;

export const AssignRoleModal: FC<IProps> = ({ isOpen, closeModal }) => {
  const { t } = useTranslation();
  const { data: userData, onRefresh: refreshUserInfo } = useContext(UserFullInfoFetcher.Context);

  const { execute: assign } = useAssignRole(userData?.id);
  const { executeWithConfig: getOrganizations } = useGetOrganizationsSilent();
  const handleError = useApiErrorHandler();

  const { data: rolesData } = useContext(RolesFetcher.Context);
  useAutoFetch(RolesFetcher);

  const orgsGetter = useCallback(
    async (params: IServerSidePaginatedFetcherParams, config: AxiosRequestConfig) => {
      const result = await getOrganizations(params, config);
      return result.payload.data;
    },
    [getOrganizations]
  );
  const { data: orgs, onSearch, isLoading: isOrgsLoading } = useSearchableDropdown(orgsGetter, isOpen);

  const orgItems = useMemo(() => orgs.map(({ name }) => name), [orgs]);

  const roleItems = useMemo(() => {
    if (!rolesData) return [];
    return rolesData.data.map((role) => ({
      value: role.id,
      label: role.name,
    }));
  }, [rolesData]);

  const form = useForm();

  const onSubmit = useCallback(
    async (data: IFormData) => {
      if (!userData) return;

      let organization_id = null;

      if (data.organization) {
        const foundOrganization = orgs.find((org) => org.name === data.organization);
        if (foundOrganization) {
          organization_id = foundOrganization.id;
        } else {
          throw new Error('Organization not found');
        }
      }

      try {
        await assign({
          role_id: data.role_id,
          user_id: userData?.id,
          organization_id,
        });
        await refreshUserInfo();
        closeModal();
        form.reset({
          organization: '',
          role_id: '',
        });
      } catch (e) {
        handleError(e);
      }
    },
    [assign, userData?.id, orgs]
  );

  return (
    <Dialog open={isOpen} onClose={closeModal} fullWidth>
      <DialogTitle>{t('userRoles.assignRole.title')}</DialogTitle>
      <DialogContent>
        <AutocompleteServersideController
          control={form.control}
          errors={form.formState.errors}
          items={orgItems}
          label={t('userRoles.assignRole.organization')}
          renderOption={(props, org) => {
            return <li {...props}>{org}</li>;
          }}
          getOptionLabel={(props) => props}
          name={'organization'}
          onSearch={onSearch}
          isLoading={isOrgsLoading}
        />

        <SelectController
          control={form.control}
          errors={form.formState.errors}
          items={roleItems}
          label={'Role'}
          name={'role_id'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal} variant={'text'}>
          {t('userRoles.assignRole.cancel')}
        </Button>
        <Button onClick={form.handleSubmit(onSubmit)} variant={'contained'}>
          {t('userRoles.assignRole.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
